import { useState, useEffect, useCallback } from 'react';
import { settingsAPI } from '@/lib/api';
import { rbacAPI, authAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import {
  Save, Plus, X, GripVertical, Globe, Copy, Check,
  Building2, Users, Sliders, Layers, FileText, Shield,
  ChevronDown, ChevronRight, ChevronUp, Trash2, Edit2, Lock, UserCog, ArrowUp, ArrowDown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import FormsBuilderSection from '@/components/FormsBuilderSection';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'select', label: 'Dropdown' },
  { value: 'date', label: 'Date' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'textarea', label: 'Long Text' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
];

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAddStatusDialog, setShowAddStatusDialog] = useState(false);
  const { user } = useAuth();
  const { refresh: refreshGlobalSettings } = useSettings();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetch = async () => {
      try {
        const sRes = await settingsAPI.get();
        setSettings(sRes.data);
      } catch {} finally { setLoading(false); }
    };
    fetch();
  }, []);

  const validateSettings = () => {
    const errors = [];

    // Company name
    if (!settings.company_name?.trim()) errors.push('Company name is required');

    // Pipeline stages
    const blankStages = (settings.pipeline_stages || []).filter(s => !s.label?.trim() || !s.key?.trim());
    if (blankStages.length > 0) errors.push('Pipeline stages cannot have blank names or keys');
    if ((settings.pipeline_stages || []).length === 0) errors.push('At least one pipeline stage is required');

    // Lead sources
    const blankSources = (settings.lead_sources || []).filter(s => !s?.trim());
    if (blankSources.length > 0) errors.push('Lead sources cannot be blank — remove empty entries or fill them in');

    // Property types
    const blankTypes = (settings.property_types || []).filter(t => !t?.trim());
    if (blankTypes.length > 0) errors.push('Property types cannot be blank');

    // Property statuses
    const blankStatuses = (settings.property_statuses || []).filter(s => {
      const label = typeof s === 'object' ? s.label : s;
      return !label?.trim();
    });
    if (blankStatuses.length > 0) errors.push('Property statuses cannot be blank');

    // Custom fields — lead
    const blankLeadFields = (settings.custom_fields_leads || []).filter(f => !f.label?.trim());
    if (blankLeadFields.length > 0) errors.push('Lead custom fields cannot have blank labels');

    // Custom fields — property
    const blankPropFields = (settings.custom_fields_properties || []).filter(f => !f.label?.trim());
    if (blankPropFields.length > 0) errors.push('Property custom fields cannot have blank labels');

    // Custom fields with dropdown type must have at least one option
    const emptyDropdowns = [
      ...(settings.custom_fields_leads || []).filter(f => f.type === 'select' && (!f.options || f.options.length === 0 || f.options.every(o => !o?.trim()))),
      ...(settings.custom_fields_properties || []).filter(f => f.type === 'select' && (!f.options || f.options.length === 0 || f.options.every(o => !o?.trim()))),
    ];
    if (emptyDropdowns.length > 0) errors.push('Dropdown custom fields must have at least one non-blank option');

    return errors;
  };

  const handleSave = async () => {
    if (!isAdmin) { toast.error('Admin only'); return; }

    const errors = validateSettings();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    // Clean data before saving — trim and remove fully blank entries
    const cleaned = {
      ...settings,
      company_name: settings.company_name?.trim(),
      lead_sources: (settings.lead_sources || []).map(s => s?.trim()).filter(Boolean),
      property_types: (settings.property_types || []).map(t => t?.trim()).filter(Boolean),
      property_statuses: (settings.property_statuses || []).filter(s => {
        const label = typeof s === 'object' ? s.label : s;
        return label?.trim();
      }),
      pipeline_stages: (settings.pipeline_stages || []).map(s => ({ ...s, label: s.label?.trim(), key: s.key?.trim() })),
      custom_fields_leads: (settings.custom_fields_leads || []).map(f => ({ ...f, label: f.label?.trim(), key: f.key?.trim() })),
      custom_fields_properties: (settings.custom_fields_properties || []).map(f => ({ ...f, label: f.label?.trim(), key: f.key?.trim() })),
    };

    setSaving(true);
    try {
      const res = await settingsAPI.update(cleaned);
      setSettings(res.data);
      refreshGlobalSettings();
      toast.success('Instellingen opgeslagen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in max-w-4xl" data-testid="settings-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Instellingen</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">Configure your CRM to match your workflow</p>
        </div>
        {isAdmin && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn" className="gap-2 flex-shrink-0">
            <Save className="w-4 h-4" /> <span className="hidden sm:inline">{saving ? 'Opslaan...' : 'Wijzigingen Opslaan'}</span><span className="sm:hidden">{saving ? '...' : 'Opslaan'}</span>
          </Button>
        )}
      </div>

      {!isAdmin && (
        <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4 text-sm text-amber-700 dark:text-amber-400">
            Only administrators can modify settings. Contact your admin for changes.
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="general" className="w-full">
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full sm:w-full sm:grid sm:grid-cols-6">
            <TabsTrigger value="general" data-testid="settings-tab-general" className="gap-1 text-xs whitespace-nowrap px-3"><Sliders className="w-3.5 h-3.5 hidden sm:block" />Algemeen</TabsTrigger>
            <TabsTrigger value="pipeline" data-testid="settings-tab-pipeline" className="gap-1 text-xs whitespace-nowrap px-3"><Layers className="w-3.5 h-3.5 hidden sm:block" />Pipeline</TabsTrigger>
            <TabsTrigger value="custom-fields" data-testid="settings-tab-custom" className="gap-1 text-xs whitespace-nowrap px-3"><FileText className="w-3.5 h-3.5 hidden sm:block" />Velden</TabsTrigger>
            <TabsTrigger value="roles" data-testid="settings-tab-roles" className="gap-1 text-xs whitespace-nowrap px-3"><Shield className="w-3.5 h-3.5 hidden sm:block" />Rollen</TabsTrigger>
            <TabsTrigger value="capture" data-testid="settings-tab-capture" className="gap-1 text-xs whitespace-nowrap px-3"><Globe className="w-3.5 h-3.5 hidden sm:block" />Formulier</TabsTrigger>
            <TabsTrigger value="sources" data-testid="settings-tab-sources" className="gap-1 text-xs whitespace-nowrap px-3"><Users className="w-3.5 h-3.5 hidden sm:block" />Bronnen</TabsTrigger>
          </TabsList>
        </div>

        {/* ─── General ─── */}
        <TabsContent value="general" className="mt-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3"><CardTitle className="text-sm">Algemene Instellingen</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Bedrijfsnaam</Label>
                  <Input value={settings.company_name || ''} onChange={(e) => setSettings(p => ({...p, company_name: e.target.value}))} disabled={!isAdmin} data-testid="company-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Valuta</Label>
                  <select value={settings.currency || 'USD'} onChange={(e) => setSettings(p => ({...p, currency: e.target.value}))} disabled={!isAdmin} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    {['USD', 'EUR', 'GBP', 'INR', 'AUD', 'CAD', 'BRL', 'JPY', 'SGD', 'AED', 'CHF', 'CNY'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Datumformaat</Label>
                  <select value={settings.date_format || 'MM/DD/YYYY'} onChange={(e) => setSettings(p => ({...p, date_format: e.target.value}))} disabled={!isAdmin} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    {['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <Switch checked={settings.auto_assignment || false} onCheckedChange={(v) => setSettings(p => ({...p, auto_assignment: v}))} disabled={!isAdmin} data-testid="auto-assignment-toggle" />
                  <Label className="text-xs">Auto-assign leads (round-robin)</Label>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Dienst Types</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(settings.property_types || []).map((t, i) => (
                      <div key={i} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                        <span className="text-xs capitalize">{t}</span>
                        {isAdmin && <button onClick={() => setSettings(p => ({...p, property_types: p.property_types.filter((_, j) => j !== i)}))}>
                          <X className="w-2.5 h-2.5 text-muted-foreground" /></button>}
                      </div>
                    ))}
                    {isAdmin && <button onClick={() => {
                      const name = prompt('New property type:');
                      if (name) setSettings(p => ({...p, property_types: [...(p.property_types || []), name.toLowerCase()]}));
                    }} className="text-xs text-primary hover:underline">+ Add</button>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Dienst Statussen</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(settings.property_statuses || []).map((s, i) => {
                      const label = typeof s === 'object' ? s.label : s;
                      const color = typeof s === 'object' ? s.color : null;
                      return (
                        <div key={i} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                          {color && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />}
                          <span className="text-xs capitalize">{label}</span>
                          {isAdmin && <button onClick={() => setSettings(p => ({...p, property_statuses: p.property_statuses.filter((_, j) => j !== i)}))}>
                            <X className="w-2.5 h-2.5 text-muted-foreground" /></button>}
                        </div>
                      );
                    })}
                    {isAdmin && <button onClick={() => setShowAddStatusDialog(true)} className="text-xs text-primary hover:underline">+ Add</button>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Pipeline ─── */}
        <TabsContent value="pipeline" className="mt-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Pipeline Fases</CardTitle>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="add-stage-btn"
                    onClick={() => setSettings(p => ({...p, pipeline_stages: [...(p.pipeline_stages || []), {key: `stage_${Date.now()}`, label: 'New Stage', color: '#6B7280'}]}))}>
                    <Plus className="w-3 h-3" />Add Stage
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-0">
              <p className="text-xs text-muted-foreground mb-3">Drag stages to reorder. Stages appear left to right on the Kanban board.</p>
              <PipelineStageList
                stages={settings.pipeline_stages || []}
                onChange={(stages) => setSettings(p => ({...p, pipeline_stages: stages}))}
                isAdmin={isAdmin}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Custom Fields ─── */}
        <TabsContent value="custom-fields" className="mt-4 space-y-4">
          <CustomFieldsSection
            title="Lead Extra Velden"
            description="Add extra fields that appear when creating or editing leads. These fields are specific to your business needs."
            icon={Users}
            fields={settings.custom_fields_leads || []}
            onChange={(fields) => setSettings(p => ({...p, custom_fields_leads: fields}))}
            isAdmin={isAdmin}
            testIdPrefix="lead"
          />
          <CustomFieldsSection
            title="Dienst Extra Velden"
            description="Add extra fields that appear when creating or editing properties. Perfect for tracking attributes unique to your market."
            icon={Building2}
            fields={settings.custom_fields_properties || []}
            onChange={(fields) => setSettings(p => ({...p, custom_fields_properties: fields}))}
            isAdmin={isAdmin}
            testIdPrefix="property"
          />
        </TabsContent>

        {/* ─── Lead Capture / Forms Builder ─── */}
        <TabsContent value="capture" className="mt-4 space-y-4">
          {/* New: dynamic forms builder */}
          <FormsBuilderSection />

          {/* Legacy: simple /capture toggle (behouden voor backwards compat) */}
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <CardTitle className="text-sm">Standaard lead-formulier (/capture)</CardTitle>
                </div>
                <Badge variant={settings.capture_form_enabled !== false ? 'default' : 'secondary'} className="text-[10px]">
                  {settings.capture_form_enabled !== false ? 'Actief' : 'Uitgeschakeld'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Publiek formulier inschakelen</p>
                  <p className="text-xs text-muted-foreground">Het oorspronkelijke generieke /capture formulier (bron: web_form)</p>
                </div>
                <Switch checked={settings.capture_form_enabled !== false} onCheckedChange={(v) => setSettings(p => ({...p, capture_form_enabled: v}))} disabled={!isAdmin} data-testid="capture-form-toggle" />
              </div>
              {settings.capture_form_enabled !== false && (
                <div className="bg-muted/50 rounded-lg p-4 border space-y-3">
                  <p className="text-xs font-medium">Share this link or embed it in your website:</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/capture`} className="h-9 text-xs mono flex-1" data-testid="capture-form-url" />
                    <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="copy-capture-url"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/capture`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Leads appear as "New" with source "web_form".</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Sources ─── */}
        <TabsContent value="sources" className="mt-4">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Leadbronnen</CardTitle>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="add-source-btn"
                    onClick={() => setSettings(p => ({...p, lead_sources: [...(p.lead_sources || []), '']}))}>
                    <Plus className="w-3 h-3" />Add Source
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">Define where your leads come from. These appear as filter options and in the lead creation form.</p>
              <div className="flex flex-wrap gap-2">
                {(settings.lead_sources || []).map((source, i) => (
                  <div key={i} className="flex items-center gap-1 bg-muted rounded-md px-2 py-1">
                    <Input value={source} onChange={(e) => {
                      const sources = [...settings.lead_sources];
                      sources[i] = e.target.value;
                      setSettings(p => ({...p, lead_sources: sources}));
                    }} className="h-7 w-28 text-xs border-0 bg-transparent p-0" disabled={!isAdmin} />
                    {isAdmin && (
                      <button onClick={() => setSettings(p => ({...p, lead_sources: p.lead_sources.filter((_, j) => j !== i)}))} className="text-muted-foreground hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Roles & Permissions ─── */}
        <TabsContent value="roles" className="mt-4">
          <RolesPermissionsTab />
        </TabsContent>
      </Tabs>

      {/* Add Property Status Dialog */}
      <AddPropertyStatusDialog
        open={showAddStatusDialog}
        onClose={() => setShowAddStatusDialog(false)}
        onAdd={(statusName, statusColor) => {
          setSettings(p => ({
            ...p, 
            property_statuses: [
              ...(p.property_statuses || []), 
              { key: statusName.toLowerCase(), label: statusName, color: statusColor || '#6B7280' }
            ]
          }));
        }}
      />
    </div>
  );
}


/* ─── Custom Fields Section ─── */
function CustomFieldsSection({ title, description, icon: Icon, fields, onChange, isAdmin, testIdPrefix }) {
  const [expandedField, setExpandedField] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(true);

  const addField = () => {
    const newField = {
      key: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
    };
    onChange([...fields, newField]);
    setExpandedField(fields.length);
    if (!sectionOpen) setSectionOpen(true);
  };

  const commitField = (index, updated) => {
    const newFields = fields.map((f, i) => i === index ? updated : f);
    onChange(newFields);
  };

  const removeField = (index) => {
    onChange(fields.filter((_, i) => i !== index));
    if (expandedField === index) setExpandedField(null);
  };

  const TYPE_LABELS = { text: 'Text', number: 'Number', select: 'Dropdown', date: 'Date', checkbox: 'Checkbox', textarea: 'Long Text', url: 'URL', email: 'Email', phone: 'Phone' };

  return (
    <Card className="border-border/60" data-testid={`${testIdPrefix}-custom-fields`}>
      <button type="button" onClick={() => setSectionOpen(!sectionOpen)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/10 transition-colors">
        <div className="flex items-center gap-2">
          {sectionOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <Icon className="w-4 h-4 text-primary" />
          <div className="text-left">
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">{fields.length} field{fields.length !== 1 ? 's' : ''}</Badge>
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); addField(); }} data-testid={`add-${testIdPrefix}-field-btn`}>
              <Plus className="w-3 h-3" /> Add
            </Button>
          )}
        </div>
      </button>
      {sectionOpen && (
        <CardContent className="pt-0 pb-4">
          {fields.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p className="text-sm">No custom fields defined yet</p>
              {isAdmin && <p className="text-xs mt-1">Click "Add" to create one</p>}
            </div>
          ) : (
            <div className="space-y-1.5">
              {fields.map((field, i) => {
                const isExpanded = expandedField === i;
                return (
                  <div key={field.key || i} className={`border rounded-lg overflow-hidden transition-all ${isExpanded ? 'border-primary/30 bg-primary/[0.02]' : 'border-border/40'}`} data-testid={`${testIdPrefix}-field-${i}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedField(isExpanded ? null : i)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/20 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <GripVertical className="w-3 h-3 text-muted-foreground/30" />
                        <span className="text-sm font-medium">{field.label || 'Untitled'}</span>
                        <Badge variant="secondary" className="text-[9px] h-4">{TYPE_LABELS[field.type] || field.type}</Badge>
                        {field.required && <Badge variant="default" className="text-[9px] h-4">Required</Badge>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] mono text-muted-foreground hidden sm:inline">{field.key}</span>
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <FieldEditor
                        field={field}
                        index={i}
                        isAdmin={isAdmin}
                        typeLabels={TYPE_LABELS}
                        onCommit={(updated) => commitField(i, updated)}
                        onRemove={() => removeField(i)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}


/* ─── Field Editor (local state to prevent re-render on each keystroke) ─── */
function FieldEditor({ field, index, isAdmin, typeLabels, onCommit, onRemove }) {
  const [local, setLocal] = useState({ ...field });

  // Sync local state when field changes externally (e.g., parent saves)
  useEffect(() => {
    setLocal({ ...field });
  }, [field]);

  const update = (key, value) => {
    const updated = { ...local, [key]: value };
    // Auto-generate key from label
    if (key === 'label') {
      const generated = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      updated.key = generated || field.key;  // keep original key if label produces empty key
    }
    setLocal(updated);
    // Don't commit immediately - only update local state
  };

  // Commit on blur for text fields
  const commitOnBlur = () => onCommit(local);

  const addOption = () => {
    const updated = { ...local, options: [...(local.options || []), ''] };
    setLocal(updated);
    onCommit(updated);
  };

  const updateOption = (optIndex, value) => {
    const opts = [...(local.options || [])];
    opts[optIndex] = value;
    setLocal({ ...local, options: opts });
    // Don't commit immediately - wait for blur
  };

  const removeOption = (optIndex) => {
    const updated = { ...local, options: (local.options || []).filter((_, i) => i !== optIndex) };
    setLocal(updated);
    onCommit(updated);
  };

  return (
    <div className="px-3 pb-3 pt-1 border-t space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] text-muted-foreground">Field Label</Label>
          <Input
            value={local.label}
            onChange={(e) => update('label', e.target.value)}
            onBlur={commitOnBlur}
            className="h-8 text-xs"
            disabled={!isAdmin}
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Type</Label>
          <select
            value={local.type}
            onChange={(e) => { update('type', e.target.value); onCommit({ ...local, type: e.target.value }); }}
            disabled={!isAdmin}
            className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm"
          >
            {Object.entries(typeLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={local.required || false}
              onCheckedChange={(v) => { update('required', v); onCommit({ ...local, required: v }); }}
              disabled={!isAdmin}
            />
            <Label className="text-[10px]">Required</Label>
          </div>
          {isAdmin && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 ml-auto text-muted-foreground hover:text-destructive" onClick={onRemove}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-muted-foreground">Placeholder</Label>
        <Input
          value={local.placeholder || ''}
          onChange={(e) => update('placeholder', e.target.value)}
          onBlur={commitOnBlur}
          className="h-7 text-xs max-w-sm"
          placeholder="Hint text shown in empty field"
          disabled={!isAdmin}
        />
      </div>
      {local.type === 'select' && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] text-muted-foreground">Dropdown Options</Label>
            {isAdmin && <button type="button" onClick={addOption} className="text-[10px] text-primary hover:underline">+ Add option</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(local.options || []).map((opt, j) => (
              <div key={j} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                <Input
                  value={opt}
                  onChange={(e) => updateOption(j, e.target.value)}
                  onBlur={() => onCommit(local)}
                  className="h-6 w-24 text-[11px] border-0 bg-transparent p-0"
                  placeholder="Option..."
                  disabled={!isAdmin}
                />
                {isAdmin && <button type="button" onClick={() => removeOption(j)} className="text-muted-foreground hover:text-destructive"><X className="w-2.5 h-2.5" /></button>}
              </div>
            ))}
            {(local.options || []).length === 0 && <p className="text-[10px] text-muted-foreground">No options yet</p>}
          </div>
        </div>
      )}
      <p className="text-[9px] text-muted-foreground mono">key: {local.key}</p>
    </div>
  );
}


/* ─── Roles & Permissions Tab ─── */
function RolesPermissionsTab() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  const [roles, setRoles] = useState({ default_roles: [], custom_roles: [] });
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [saving, setSaving] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [rRes, pRes, uRes] = await Promise.all([
        rbacAPI.getRoles(),
        rbacAPI.getPermissions(),
        authAPI.users().catch(() => ({ data: [] })),
      ]);
      setRoles(rRes.data);
      setSections(pRes.data.sections || []);
      setUsers(uRes.data || []);
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allRoles = [...(roles.default_roles || []), ...(roles.custom_roles || [])];

  const [roleError, setRoleError] = useState('');

  const validateRoleName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'Role name is required';
    if (trimmed.length < 2) return 'Role name must be at least 2 characters';
    if (trimmed.length > 50) return 'Role name must be under 50 characters';
    if (!/[A-Za-z]/.test(trimmed)) return 'Role name must contain at least one letter';
    if (!/^[A-Za-z0-9\s\-]+$/.test(trimmed)) return 'Role name can only contain letters, numbers, spaces, and hyphens';
    if (/[\-]{2,}/.test(trimmed)) return 'Role name cannot have consecutive hyphens';
    return null;
  };

  const handleCreateRole = async () => {
    const err = validateRoleName(newRoleName);
    if (err) { setRoleError(err); return; }
    setSaving(true);
    setRoleError('');
    try {
      await rbacAPI.createRole({ name: newRoleName.trim(), description: newRoleDesc.trim() });
      toast.success('Role created');
      setShowCreate(false); setNewRoleName(''); setNewRoleDesc(''); setRoleError('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleSavePermissions = async () => {
    if (!editingRole || editingRole.is_default) return;
    setSaving(true);
    try {
      await rbacAPI.updateRole(editingRole.id, {
        name: editingRole.name,
        description: editingRole.description,
        permissions: editingRole.permissions,
      });
      toast.success('Permissions saved');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await rbacAPI.deleteRole(roleId);
      toast.success('Role deleted');
      if (selectedRole?.id === roleId) { setSelectedRole(null); setEditingRole(null); }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      await rbacAPI.assignRole(userId, roleId);
      toast.success('Role assigned');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const togglePermission = (key) => {
    if (!editingRole || editingRole.is_default) return;
    setEditingRole(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] }
    }));
  };

  const toggleSection = (sectionPerms, allEnabled) => {
    if (!editingRole || editingRole.is_default) return;
    const updates = {};
    sectionPerms.forEach(p => { updates[p.key] = !allEnabled; });
    setEditingRole(prev => ({
      ...prev,
      permissions: { ...prev.permissions, ...updates }
    }));
  };

  if (loading) return <div className="h-32 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const viewRole = editingRole || selectedRole;

  return (
    <div className="space-y-4" data-testid="roles-permissions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Roles & Permissions</h3>
          <p className="text-xs text-muted-foreground">Manage access control for your team</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setShowCreateUser(true)} className="gap-1.5" data-testid="create-user-btn">
              <UserCog className="w-3.5 h-3.5" /> Nieuwe gebruiker
            </Button>
          )}
          <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5" data-testid="create-role-btn">
            <Plus className="w-3.5 h-3.5" /> New Role
          </Button>
        </div>
      </div>

      {/* Create Role Inline */}
      {showCreate && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Role Name *</Label>
                <Input value={newRoleName} onChange={(e) => { setNewRoleName(e.target.value); setRoleError(''); }} placeholder="e.g. Senior Agent, Team Lead..." className={`h-8 text-xs ${roleError ? 'border-destructive' : ''}`} data-testid="new-role-name" />
                {roleError && <p className="text-[10px] text-destructive mt-0.5">{roleError}</p>}
              </div>
              <div><Label className="text-xs">Description</Label><Input value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} placeholder="Brief description..." className="h-8 text-xs" /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateRole} disabled={saving} className="h-7 text-xs" data-testid="save-new-role">{saving ? 'Creating...' : 'Create'}</Button>
              <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setRoleError(''); }} className="h-7 text-xs">Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Role List */}
        <div className="space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Roles</p>
          {allRoles.map((role) => {
            const isSelected = viewRole?.id === role.id;
            const userCount = users.filter(u => u.role === role.id).length;
            return (
              <div
                key={role.id}
                onClick={() => { setSelectedRole(role); setEditingRole(role.is_default ? null : { ...role }); }}
                data-testid={`role-item-${role.id}`}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-primary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {role.is_default ? <Lock className="w-3.5 h-3.5 text-muted-foreground" /> : <Shield className="w-3.5 h-3.5 text-primary" />}
                    <span className="text-sm font-medium">{role.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="secondary" className="text-[9px]">{userCount} user{userCount !== 1 ? 's' : ''}</Badge>
                    {!role.is_default && (
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }} className="text-muted-foreground hover:text-destructive p-0.5">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{role.description}</p>
                {role.is_default && <Badge variant="secondary" className="text-[8px] mt-1">Default — Read only</Badge>}
              </div>
            );
          })}
        </div>

        {/* Permission Editor */}
        <div className="lg:col-span-2">
          {viewRole ? (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      {viewRole.is_default ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Shield className="w-4 h-4 text-primary" />}
                      {viewRole.name} Permissions
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{viewRole.description}</p>
                  </div>
                  {editingRole && !editingRole.is_default && (
                    <Button size="sm" onClick={handleSavePermissions} disabled={saving} className="gap-1.5" data-testid="save-permissions-btn">
                      <Save className="w-3.5 h-3.5" /> {saving ? 'Saving...' : 'Save'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1 max-h-[500px] overflow-y-auto">
                {sections.map((section) => {
                  const perms = viewRole.is_default ? (selectedRole?.permissions || {}) : (editingRole?.permissions || {});
                  const sectionPerms = section.permissions;
                  const enabledCount = sectionPerms.filter(p => perms[p.key]).length;
                  const allEnabled = enabledCount === sectionPerms.length;
                  return (
                    <PermissionSection
                      key={section.key}
                      section={section}
                      permissions={perms}
                      isReadOnly={viewRole.is_default}
                      enabledCount={enabledCount}
                      allEnabled={allEnabled}
                      onToggle={togglePermission}
                      onToggleSection={() => toggleSection(sectionPerms, allEnabled)}
                    />
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/60">
              <CardContent className="p-12 text-center text-muted-foreground">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Select a role to view or edit permissions</p>
              </CardContent>
            </Card>
          )}

          {/* Users with this role */}
          {viewRole && (
            <Card className="border-border/60 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><UserCog className="w-4 h-4" />Users with "{viewRole.name}" role</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Warning for empty permissions */}
                {!viewRole.is_default && viewRole.permissions && Object.values(viewRole.permissions).filter(Boolean).length === 0 && (
                  <div className="mb-3 p-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                    <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">No permissions configured</p>
                      <p className="mt-0.5">This role has zero permissions enabled. Users assigned to it will default to Agent-level access. Configure permissions above before assigning users.</p>
                    </div>
                  </div>
                )}
                {/* Assign user to this role */}
                {!viewRole.is_default && (
                  <div className="mb-3 flex items-center gap-2">
                    <select
                      defaultValue=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        const permsCount = editingRole?.permissions ? Object.values(editingRole.permissions).filter(Boolean).length : 0;
                        if (permsCount === 0) {
                          const proceed = window.confirm('This role has no permissions configured. The user will get default Agent-level access. Continue?');
                          if (!proceed) { e.target.value = ''; return; }
                        }
                        await handleAssignRole(e.target.value, viewRole.id);
                        e.target.value = '';
                      }}
                      className="flex-1 h-8 text-xs rounded-md border border-input bg-background px-2"
                      data-testid="assign-user-to-role"
                    >
                      <option value="">Assign a user to this role...</option>
                      {users.filter(u => u.role !== viewRole.id).map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name} ({u.email}) — currently {allRoles.find(r => r.id === u.role)?.name || u.role}</option>
                      ))}
                    </select>
                  </div>
                )}
                {users.filter(u => u.role === viewRole.id).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">No users assigned yet{!viewRole.is_default ? ' — use the dropdown above to assign users' : ''}</p>
                ) : (
                  <div className="space-y-2">
                    {users.filter(u => u.role === viewRole.id).map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary">{u.name?.charAt(0)}</div>
                          <div><p className="text-xs font-medium">{u.name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></div>
                        </div>
                        <select
                          value={u.role}
                          onChange={(e) => handleAssignRole(u.user_id, e.target.value)}
                          className="h-7 text-[10px] rounded border border-input bg-background px-2"
                        >
                          {allRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Create User Dialog (admin only) */}
      <CreateUserDialog
        open={showCreateUser}
        onClose={() => setShowCreateUser(false)}
        availableRoles={allRoles}
        onCreated={() => {
          setShowCreateUser(false);
          fetchData();
        }}
      />
    </div>
  );
}


function CreateUserDialog({ open, onClose, availableRoles, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState('agent');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setName(''); setEmail(''); setPassword(''); setConfirmPassword('');
    setRole('agent'); setError(''); setSubmitting(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!name.trim() || name.trim().length < 2) {
      setError('Naam moet minstens 2 tekens bevatten');
      return;
    }
    if (!email.trim()) {
      setError('E-mail is verplicht');
      return;
    }
    if (password.length < 8) {
      setError('Wachtwoord moet minstens 8 tekens bevatten');
      return;
    }
    if (password !== confirmPassword) {
      setError('Wachtwoorden komen niet overeen');
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.adminCreateUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        role,
      });
      toast.success(`Account aangemaakt voor ${name.trim()}`);
      reset();
      onCreated();
    } catch (err) {
      const msg = err.response?.data?.detail || 'Account aanmaken mislukt';
      setError(typeof msg === 'string' ? msg : 'Account aanmaken mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent className="max-w-md" data-testid="create-user-dialog">
        <DialogHeader>
          <DialogTitle>Nieuwe gebruiker aanmaken</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label className="text-xs">Volledige naam *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jan de Vries"
              data-testid="new-user-name"
              autoFocus
              required
            />
          </div>
          <div>
            <Label className="text-xs">E-mail *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jan@bedrijf.nl"
              data-testid="new-user-email"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Wachtwoord *</Label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 tekens"
                data-testid="new-user-password"
                required
                minLength={8}
              />
            </div>
            <div>
              <Label className="text-xs">Bevestig wachtwoord *</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Herhaal wachtwoord"
                data-testid="new-user-password-confirm"
                required
                minLength={8}
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Rol</Label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              data-testid="new-user-role"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm"
            >
              {(availableRoles || []).map((r) => (
                <option key={r.id} value={r.id}>{r.name}</option>
              ))}
            </select>
          </div>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>Annuleer</Button>
            <Button type="submit" disabled={submitting} data-testid="submit-create-user">
              {submitting ? 'Bezig...' : 'Account aanmaken'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function PermissionSection({ section, permissions, isReadOnly, enabledCount, allEnabled, onToggle, onToggleSection }) {
  const [open, setOpen] = useState(false);
  const total = section.permissions.length;

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`perm-section-${section.key}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <span className="text-xs font-semibold">{section.label}</span>
          <Badge variant={enabledCount === total ? 'default' : enabledCount > 0 ? 'secondary' : 'outline'} className="text-[8px] h-4">
            {enabledCount}/{total}
          </Badge>
        </div>
        {!isReadOnly && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSection(); }}
            className="text-[10px] text-primary hover:underline"
          >
            {allEnabled ? 'Disable all' : 'Enable all'}
          </button>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 border-t bg-muted/10">
          {section.permissions.map((perm) => {
            const enabled = !!permissions[perm.key];
            return (
              <label
                key={perm.key}
                className={`flex items-center gap-3 py-1.5 px-2 rounded-md transition-colors ${
                  isReadOnly ? '' : 'cursor-pointer hover:bg-muted/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => !isReadOnly && onToggle(perm.key)}
                  disabled={isReadOnly}
                  className="rounded border-input h-3.5 w-3.5 accent-primary"
                  data-testid={`perm-${perm.key}`}
                />
                <span className={`text-xs ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{perm.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}



/* ─── Drag-and-Drop Pipeline Stage List ─── */
function PipelineStageList({ stages, onChange, isAdmin }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const updated = [...stages];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    onChange(updated);
    setDragIndex(null);
    setOverIndex(null);
  };

  const updateStage = (index, field, value) => {
    const updated = [...stages];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeStage = (index) => {
    onChange(stages.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-0">
      {stages.map((stage, i) => (
        <div
          key={stage.key || i}
          draggable={isAdmin}
          onDragStart={(e) => handleDragStart(e, i)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          className={`flex flex-wrap sm:flex-nowrap items-center gap-2 group py-2 px-1 rounded-md transition-all ${
            isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
          } ${overIndex === i && dragIndex !== i ? 'bg-primary/10 border-primary/30 border-dashed border' : 'border border-transparent'
          } ${dragIndex === i ? 'opacity-50' : ''}`}
          data-testid={`stage-row-${i}`}
        >
          <GripVertical className={`w-4 h-4 flex-shrink-0 ${isAdmin ? 'text-muted-foreground/50 group-hover:text-muted-foreground' : 'text-muted-foreground/20'}`} />
          <span className="text-[10px] text-muted-foreground w-5 text-center flex-shrink-0">{i + 1}</span>
          <input
            type="color"
            value={stage.color}
            onChange={(e) => updateStage(i, 'color', e.target.value)}
            className="w-8 h-8 rounded border cursor-pointer flex-shrink-0"
            disabled={!isAdmin}
          />
          <Input
            value={stage.label}
            onChange={(e) => updateStage(i, 'label', e.target.value)}
            className="flex-1 h-9"
            disabled={!isAdmin}
            data-testid={`stage-label-${i}`}
          />
          <Input
            value={stage.key}
            onChange={(e) => updateStage(i, 'key', e.target.value)}
            className="w-full sm:w-32 h-9 mono text-xs"
            disabled={!isAdmin}
            placeholder="key"
          />
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => removeStage(i)}
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}



/* ─── Add Property Status Dialog ─── */
function AddPropertyStatusDialog({ open, onClose, onAdd }) {
  const [statusName, setStatusName] = useState('');
  const [statusColor, setStatusColor] = useState('#6B7280');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (statusName.trim()) {
      onAdd(statusName.trim(), statusColor);
      setStatusName('');
      setStatusColor('#6B7280');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Property Status</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Status Name *</Label>
            <Input
              value={statusName}
              onChange={(e) => setStatusName(e.target.value)}
              placeholder="e.g., Available, Sold, Under Offer"
              autoFocus
              required
            />
          </div>
          <div>
            <Label>Color</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                className="w-12 h-10 rounded border cursor-pointer"
              />
              <Input
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                placeholder="#6B7280"
                className="flex-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">Add Status</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
